"use client";

import { useFormatNumber } from "@/lib/i18n/format";
import { DarkButton } from "../hero-primitives";
import { BrandLogoMark } from "@v1/ui/brand-logo-mark";
import { useTranslations } from "next-intl";
import type { CSSProperties, ReactNode } from "react";
import { useEffect, useMemo, useRef } from "react";

import {
  ABSORB,
  CARD,
  DEFEATED_TOOLS,
  type DefeatedToolKey,
  LIST_FROM,
  TILE,
} from "./defeated-tools-data";
import "./defeated-tools.css";

/** ms after play when card `i` has vanished into the tile. */
const absorbedAt = (i: number): number => ABSORB.start + i * ABSORB.step + ABSORB.fly;
const TOTAL_MS = absorbedAt(DEFEATED_TOOLS.length - 1) + ABSORB.settle;
/** Tile scale after `n` absorbed cards: small and quiet → big and dominant. */
const tileScale = (n: number): number =>
  TILE.from + ((1 - TILE.from) * n) / DEFEATED_TOOLS.length;

/** Running total after each absorption, so the counter can step without re-rendering. */
const RUNNING = DEFEATED_TOOLS.reduce<number[]>((acc, tool) => {
  acc.push((acc[acc.length - 1] ?? 0) + tool.price);
  return acc;
}, []);

/** Card flies to the tile center, shrinks and vanishes there. */
function absorbKeyframes(x: number, y: number, rot: number): Keyframe[] {
  // Translate in % of the card's own box: unit-free, scales with the stage.
  const dx = ((TILE.cx - (x + CARD.w / 2)) / CARD.w) * 100;
  const dy = ((TILE.cy - (y + CARD.h / 2)) / CARD.h) * 100;
  return [
    { transform: `translate(0%, 0%) rotate(${rot}deg) scale(1)`, opacity: 1, easing: "cubic-bezier(0.55, 0, 0.8, 0.3)" },
    { opacity: 1, offset: 0.7 },
    { transform: `translate(${dx}%, ${dy}%) rotate(0deg) scale(0.08)`, opacity: 0 },
  ];
}

/**
 * Plays once the stage is mostly in view; rewinds only after leaving downward, so coming
 * back from below keeps the end state. Scrolling never gets locked.
 */
function useAbsorb(
  stageRef: { readonly current: HTMLDivElement | null },
  counterTexts: readonly string[],
): void {
  useEffect(() => {
    const stage = stageRef.current;
    if (stage === null) return;
    const counter = stage.querySelector<HTMLElement>("[data-counter]");
    // List and CTA sit below the stage, in the same wrap.
    const wrap = stage.parentElement;
    const gone = Array.from(wrap?.querySelectorAll<HTMLElement>("[data-gone]") ?? []);
    const cta = wrap?.querySelector<HTMLElement>("[data-cta]") ?? null;
    const setCount = (n: number): void => {
      if (counter !== null) {
        counter.dataset.on = n > 0 ? "1" : "0";
        counter.textContent = counterTexts[n] ?? "";
      }
      gone.forEach((el, i) => {
        el.dataset.on = n >= LIST_FROM && i < n ? "1" : "0";
      });
      if (cta !== null) cta.dataset.on = n >= DEFEATED_TOOLS.length ? "1" : "0";
    };
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      stage.classList.add("mkdt-done");
      setCount(DEFEATED_TOOLS.length);
      return;
    }

    const anims: Animation[] = [];
    stage.querySelectorAll<HTMLElement>("[data-tool]").forEach((el, i) => {
      const tool = DEFEATED_TOOLS[i];
      if (tool === undefined) return;
      const anim = el.animate(absorbKeyframes(tool.x, tool.y, tool.rot), {
        delay: ABSORB.start + i * ABSORB.step,
        duration: ABSORB.fly,
        fill: "both",
      });
      anim.pause();
      anims.push(anim);
    });
    const tile = stage.querySelector("[data-tile]");
    const tileAnim =
      tile === null
        ? null
        : tile.animate(
            [
              { transform: `scale(${tileScale(0)})`, offset: 0 },
              ...DEFEATED_TOOLS.map((_, i) => ({
                transform: `scale(${tileScale(i + 1)})`,
                offset: absorbedAt(i) / TOTAL_MS,
                easing: "cubic-bezier(0.2, 1.6, 0.4, 1)",
              })),
              { transform: "scale(1)", offset: 1 },
            ],
            { duration: TOTAL_MS, fill: "both" },
          );
    tileAnim?.pause();
    const glow = stage.querySelector("[data-glow]");
    const glowAnim = glow?.animate([{ opacity: 0.1 }, { opacity: 1 }], { duration: TOTAL_MS, fill: "both" });
    glowAnim?.pause();
    const all = [...anims, ...(tileAnim ? [tileAnim] : []), ...(glowAnim ? [glowAnim] : [])];

    let raf = 0;
    const tick = (): void => {
      const now = Number(tileAnim?.currentTime ?? 0);
      setCount(DEFEATED_TOOLS.filter((_, i) => absorbedAt(i) <= now).length);
      raf = now < TOTAL_MS ? window.requestAnimationFrame(tick) : 0;
    };
    let played = false;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (entry === undefined) return;
        if (!played && entry.intersectionRatio >= 0.85) {
          played = true;
          for (const anim of all) anim.play();
          raf = window.requestAnimationFrame(tick);
        } else if (played && !entry.isIntersecting && entry.boundingClientRect.top > 0) {
          // Left downward (scrolled back above it): next pass from above replays.
          played = false;
          window.cancelAnimationFrame(raf);
          for (const anim of all) {
            anim.pause();
            anim.currentTime = 0;
          }
          setCount(0);
        }
      },
      { threshold: [0, 0.85] },
    );
    observer.observe(stage);

    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(raf);
      for (const anim of all) anim.cancel();
    };
  }, [stageRef, counterTexts]);
}

/** Tool windows around our tile; each is sucked in, the tile grows, the amount climbs, the list fills. */
export function DefeatedToolsStage(): ReactNode {
  const t = useTranslations("marketing.defeatedTools");
  const fmt = useFormatNumber();
  const stageRef = useRef<HTMLDivElement | null>(null);
  const counterTexts = useMemo(
    () => [0, ...RUNNING].map((sum) => t("counter", { amount: fmt.decimal(sum, 0) })),
    [t, fmt],
  );
  useAbsorb(stageRef, counterTexts);

  const labels: Record<DefeatedToolKey, string> = {
    erpSystem: t("erpSystem"),
    dms: t("dms"),
    outlook: t("outlook"),
    portalloesung: t("portalloesung"),
    telefonanlage: t("telefonanlage"),
    kiAnrufbeantworter: t("kiAnrufbeantworter"),
    onlineEtv: t("onlineEtv"),
    teams: t("teams"),
    uebergabeformulare: t("uebergabeformulare"),
  };

  return (
    <div className="mkdt-wrap">
      <div ref={stageRef} className="mkdt-stage">
        <ul className="mkdt-pile" style={{ margin: 0, padding: 0, listStyle: "none" }}>
          {DEFEATED_TOOLS.map((tool) => {
            const pos = { "--x": tool.x, "--y": tool.y, "--r": `${tool.rot}deg` } as CSSProperties;
            return (
              <li key={tool.key} className="mkdt-card" data-tool="" style={pos}>
                <div className="mkdt-bar" aria-hidden="true">
                  <span className="mkdt-dot" />
                  <span className="mkdt-dot" />
                  <span className="mkdt-dot" />
                </div>
                <div className="mkdt-body">
                  <span className="mkdt-label">{labels[tool.key]}</span>
                  {tool.price > 0 ? (
                    <span className="mkdt-price">{t("price", { amount: fmt.decimal(tool.price, 0) })}</span>
                  ) : (
                    <span className="mkdt-sk" style={{ width: "62%" }} aria-hidden="true" />
                  )}
                </div>
              </li>
            );
          })}
        </ul>
        <div className="mkdt-top" data-tile="">
          <div className="mkdt-halo" data-glow="" aria-hidden="true" />
          <BrandLogoMark width={330} height={330} variant="dark" />
        </div>
        <div className="mkdt-counter-box">
          <span className="mkdt-counter" data-counter="" data-on="0" aria-live="off">
            {counterTexts[counterTexts.length - 1]}
          </span>
        </div>
      </div>
      {/* In flow after the stage: on phones it wraps to more rows and pushes the CTA down. */}
      <ul className="mkdt-gone">
        {DEFEATED_TOOLS.map((tool) => (
          <li key={tool.key} data-gone="" data-on="0">
            {tool.price > 0
              ? t("gonePriced", { name: labels[tool.key], amount: fmt.decimal(tool.price, 0) })
              : labels[tool.key]}
          </li>
        ))}
      </ul>
      <div className="mkdt-cta" data-cta="" data-on="0">
        <DarkButton href="/demo" tone="cta">
          {t("demoCall")}
        </DarkButton>
      </div>
    </div>
  );
}
